//================================================================
//  Tab: Applications
//================================================================

//  Purpose: This tab displays all 'Applications' with the status of 'Active'

//  Example:
//    <Applications></Applications>    

//================================================================

//Libraries
import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

//Contexts
import { SetSortFilters, GetUser, GetRoles } from '../../../Library/GlobalContexts';

//Components
import PageComponent from '../../../Components/PageComponent/PageComponent';
import ExportToCSV from '../../../Components/ExportToCSV/ExportToCSV';
import StatusLabel from '../../../Components/StatusLabel/StatusLabel';
import AddApplication from './SidePanes/AddApplication';
import LabelList from '../../../Components/LabelList/LabelList';
import UserPane from '../../../Components/UserPane/UserPane';
import TableHeader from '../../../Components/SortFilter/TableHeader';
import FilterTags from '../../../Components/SortFilter/FilterTags';
import FilterByPane from '../../../Components/SortFilter/FilterByPane';

//Functions
import QueryListener from '../../../Library/QueryListener';
import CheckInventoryRole from '../../../Library/CheckInventoryRole';

//Images
import IconSearch from '../../../Components/Images/Icon_Search_Grey.svg';
import IconAdd from '../../../Components/Images/Icon_Add_White.svg';


export default function Applications() {

  //------------------------------------------------------
  //  useContext & React Router
  //------------------------------------------------------

  const navigate = useNavigate();

  const getUser = useContext(GetUser);
  const getRoles = useContext(GetRoles);

  const setSortFilters = useContext(SetSortFilters);

  //------------------------------------------------------
  //  useStates
  //------------------------------------------------------

  // Used to save page status > 'pending', 'onload', 'error-invalid', 'error-fatal', 'access-denied'
  const [pageStatus, setPageStatus] = useState('pending');
  const [roleStatus, setRoleStatus] = useState('read'); // read, download, create, write & admin

  // Search Input
  const [searchInput, setSearchInput] = useState('');

  // Used to store all applications
  const [applications, setApplications] = useState([]);
  const [applicationsFiltered, setApplicationsFiltered] = useState([]);

  // Current Filter > 'Draft', 'Active', 'Divest', 'Dormant', 'Decommissioned' & 'All'
  const [currentFilter, setCurrentFilter] = useState('Active');

  // Open or Close the Application or Users side pane
  const [businessOwnerPane, setBusinessOwnerPane] = useState(undefined);
  const [ITOwnerPane, setITOwnerPane] = useState(undefined);
  const [subscriberPane, setSubscriberPane] = useState(undefined);

  //------------------------------------------------------
  //  Functions
  //------------------------------------------------------ 

  // Handles search function
  function handleSearch(value) {

    setSearchInput(value);

    // Clear ALL filters & sorting
    if (value.length > 0 && value.length < 2) {

      setSortFilters({

        // Sorting
        'sortBy': '', // vendor.vendorname
        'sortDirection': '', // ascending & descending
        'sortDataType': '', // '' or 'number'

        // Filtering (Map of objects)
        'filters': {},

        // Sidepane for selecting filterby content
        'filterByPane': {
          'columnname': '', // Vendor Name
          'columnid': '', // vendor.vendorname
        }

      });

    }

    // Reset the Filter
    if (value.length === 0) {

      setApplicationsFiltered(applications);

    };

    // Search Filter
    //  1. applicationname
    //  2. vendor.vendorname
    //  3. businessowner
    //  4. itowner
    //  5. subscribed
    const searchResults = applications.filter((object) =>
      object?.applicationname?.toLowerCase().includes(value.toLowerCase()) ||
      object?.vendor?.vendorname?.toLowerCase().includes(value.toLowerCase()) ||
      object?.businessowner?.toString().toLowerCase().includes(value.toLowerCase()) ||
      object?.itowner?.toString().toLowerCase().includes(value.toLowerCase()) ||
      object?.subscribed?.toString().toLowerCase().includes(value.toLowerCase())
    );

    // Save the filtered search results to the useState
    setApplicationsFiltered(searchResults);

  };

  // Create data format for CSV Export
  function getDataObject(applications) {

    const data = [];

    applications.forEach((app) => {

      data.push({
        'Application Name': app.applicationname,
        'Description': app.applicationdescription,
        'Primary Beneficiary': app.primarybeneficiary,
        'Vendor': app.vendor.vendorname,
        'Business Owner': app.businessowner,
        'IT Owner': app.itowner,
        'Supported By': app.supportedby,
        'Status': app.applicationstatus,
      });

    });

    return data;

  };

  // Create data format for CSV Export for the Businesss
  function getBulkExportData(applications) {

    const data = [];

    applications.forEach((app) => {

      const linkedRequestList = [];
      const agreementList = [];

      // Get linked request IDs
      Object.keys(app?.linkedrequests).forEach((request) => {
        linkedRequestList.push(request);
      });

      // Get linked agreement IDs
      app?.agreements?.forEach((agreement) => {
        agreementList.push(agreement.agreementid);
      });

      data.push({
        'applicationid': app?.applicationid, // <BASE64.string(applicationname)>-<timestamp>,
        'linkedrequests': linkedRequestList,
        'applicationstatus': app?.applicationstatus, // 'Draft', 'Active', 'Decommissioned', 'Archived', 'Divest', 'Duplicate' & 'Dormant'
        'createdby': app?.createdby, // Email Address
        'createdbyname': app?.createdbyname, // Full Name (i.e. Nowshin Hassan)
        'createddate': app?.createddate.toDate(), // Timestamp, added by the system not a user editable field
        'lastmodifieddate': app?.lastmodifieddate.toDate(), // Timestamp, added by the system not a user editable field
        'lastmodifiedby': app?.lastmodifiedby?.email,
        'agreements': agreementList,
        'applicationname': app?.applicationname,
        'searcharray': app?.searcharray, // Application Name spread into an array
        'applicationdescription': app?.applicationdescription < 200 ? app?.applicationdescription : app?.applicationdescription.slice(0, 200) + '...', // limit the amount of content
        'vendorname': app?.vendor?.vendorname,
        'vendorid': app?.vendor?.vendorid,
        'itowner': app?.itowner,
        'businessowner': app?.businessowner,
        'defaultapp': app?.defaultapp,
        'usedby': app?.usedby,
        'subscribed': app?.subscribed,
        'nooflicensespurchased': app?.nooflicensespurchased,
        'type': app?.type, // 'COTS', 'Product', 'Product', 'Integration', 'Custom', & 'N/A'   
        'deploymentname': app?.deploymentname, // The name of the licenced instanced, for example, Aconex AU1 is for non defence users, Aconex AU2 is for defence users.
        'deploymenttype': app?.deploymenttype, // 'Web Application', 'Desktop Application', 'Mobile Application', 'Server Application' & 'N/A'
        'capabilityfamily': app?.capabilityfamily,
        'supportedby': app?.supportedby,
        'supportlevelinternal': app?.supportlevelinternal,
        'supportlevelexternal': app?.supportlevelexternal,
        'ssoimplemented': app?.ssoimplemented, // 'Yes', 'No' & 'N/A'
        'mfaimplemented': app?.mfaimplemented, // 'Yes', 'No' & 'N/A'
        'typeofcost': app?.typeofcost, // 'Business Cost', 'Functional Technology Cost' & 'Enterprise IT'
        'budgetowner': app?.budgetowner,
        'projectcode': app?.projectcode,
        'tasknumber': app?.tasknumber,
        'expendituretype': app?.expendituretype,
        'licencetype': app?.licencetype,  // 'Subscription', 'Perpetual', 'Concurrent' & 'Other'
        'costallocationcurrency': app?.costallocationcurrency, // 'USD', 'SGD', 'GBP', 'AUD' & 'MYR',
        'bcp': app?.bcp, // 'Yes', 'No' & 'N/A'
        'rpo': app?.rpo, // Used for "Data Recovery Point" 'Tier 0', 'Tier 1', 'Tier 2', 'Tier 3', 'Tier 4', 'Tier 5', 'Tier 6'
        'rto': app?.rto, // 'Tier 0', 'Tier 1', 'Tier 2', 'Tier 3', 'Tier 4', 'Tier 5', 'Tier 6'  
        'linktodr': app?.linktodr, // Website URL
        'itmanaged': app?.itmanaged, // Yes or No
        'itcommerciallymanaged': app?.itcommerciallymanaged, // Yes or No
        'itbudgeted': app?.itbudgeted, // Yes or No
        'primarybeneficiary': app?.primarybeneficiary,                   // Choice - 'Investment Management', 'Construction', 'Development', 'Functions', 'Digital'
        'region': app?.region,
        'applicationcategories': app?.applicationcategories,
        'environment': app?.environment,   // 'Prod', 'UAT/pre-prod', 'SIT', 'Dev', 'DR'
        'appserver': app?.appserver,
        'otherserver': app?.otherserver,
        'softwarerequirements': app?.softwarerequirements,
        'dnsnames': app?.dnsnames,
        'serviceaccounts': app?.serviceaccounts,
        'gcpproject': app?.gcpproject,
        'primaryhostinglocation': app?.primaryhostinglocation,
        'secondaryhostinglocation': app?.secondaryhostinglocation,
        'applicationurl': app?.applicationurl,
        'osversion': app?.osversion,
        'mtohours': app?.mtohours, //  Choice - 'Tier 0', 'Tier 1', 'Tier 2', 'Tier 3', 'Tier 4', 'Tier 5', 'Tier 6'  
        'drfrequency': app?.drfrequency, // Choice -  'Annually, Semi-Annually
        'applicablecontrols': app?.applicablecontrols,
        'dataclassification': app?.dataclassification, // Choice - 'Public', 'Internal', 'Confidential', 'Restricted'
        'contractenddate': app?.contractenddate, // Lookup - from Agreements, see 'agreementenddate' field
        'numberofusers': app?.numberofusers, // Number
        'visibleinjsm': app?.visibleinjsm, // Choice - 'Yes', 'No'
        'supportassignmentgroup': app?.supportassignmentgroup, // TODO - check if this should be an array or string
        'supportrenewaldate': app?.supportrenewaldate,
        'workflows': app?.workflows,
        'useraccessreview': app?.useraccessreview, // Choice - 'Monthly' 'Quarterly' 'Semi-Annual' or 'Annual'
        'prodbackup': app?.prodbackup, // Choice - 'Daily', 'Weekly', 'Monthly' 'Quarterly' 'Semi-Annual' or 'Annual'
        'slareporting': app?.slareporting, // Choice - 'Monthly' 'Quarterly' 'Semi-Annual' or 'Annual'
        'supportdl': app?.supportdl,
        'decommissiondate': app?.decommissiondata?.decommissiondate,
        'dataretention': app?.decommissiondata?.dataretention,
        'decommissiondataclassification': app?.decommissiondata?.dataclassification,
        'datalocation': app?.decommissiondata?.datalocation,
        'nextcompreviewdate': app?.nextcompreviewdate, // Date
        'assettype': app?.assettype, // Application, Technology, Interface / Integration & Information
        'confidentialityrating': app?.confidentialityrating, // Choice - 1 to 5
        'integrityrating': app?.integrityrating, // Choice - 1 to 5
        'availabilityrating': app?.availabilityrating, // Choice - 1 to 5
        'assetscore': app?.assetscore, // 0 to 100
        'sensitivityrating': app?.sensitivityrating, // Choice - 1 to 5
        'assetier': app?.assetier, // Choice - 1 to 5
        'internetfacing': app?.internetfacing, // Choice - Yes, No & N/A 
        'mva': app?.mva, // Yes & No
      });

    });

    return data;

  };

  //------------------------------------------------------
  //  useEffects
  //------------------------------------------------------

  // Query Listener
  // - Get all applications with the status of 'Active'
  useEffect(() => {

    if (currentFilter === undefined) return;
    if (getRoles === undefined) return;
    if (getUser === undefined) return;

    // ============================================
    //  Perform role check
    // ============================================

    const roleStatus = CheckInventoryRole('applications', getRoles, getUser);
    if (roleStatus === 'access-denied') return setPageStatus('access-denied');
    setRoleStatus(roleStatus);

    // ============================================
    //  Fetch Data
    // ============================================

    function onLoad(documents) {

      setApplications(documents);
      setApplicationsFiltered(documents);
      setSearchInput('');

      setPageStatus('onload');

    }

    function onChange(documents) {

      setApplications(documents);
      setSearchInput('');

    }

    function onError(error) {

      console.log(error);

      setPageStatus('error-fatal');

    }

    const unsubscribe = QueryListener(
      'applications',
      currentFilter === 'All' ? [] : [['applicationstatus', '==', currentFilter]],
      onLoad,
      onChange,
      onError,
      ['applicationname', 'asc']
    );

    return () => {

      unsubscribe();

    };

    // eslint-disable-next-line
  }, [currentFilter, getRoles, getUser]);

  //------------------------------------------------------
  //  HTML
  //------------------------------------------------------

  return (

    <PageComponent
      status={pageStatus}
      body={

        <div className='flex flex-col px-[15px] gap-[20px] w-[calc(100vw-40px)] max-h-[calc(100vh-220px)] overflow-hidden'>

          {/* ----------------------------------- */}
          {/*  Search & Filters                   */}
          {/* ----------------------------------- */}

          <div className='flex flex-row justify-between items-center gap-3'>

            <div className='flex flex-row items-center gap-2'>

              {/* Active */}
              <button className={currentFilter === 'Active' ? 'Filter-Primary' : 'Filter-Secondary'} onClick={() => setCurrentFilter('Active')}>
                Active
              </button>

              {/* Divest */}
              <button className={currentFilter === 'Divest' ? 'Filter-Primary' : 'Filter-Secondary'} onClick={() => setCurrentFilter('Divest')}>
                Divest
              </button>

              {/* Dormant */}
              <button className={currentFilter === 'Dormant' ? 'Filter-Primary' : 'Filter-Secondary'} onClick={() => setCurrentFilter('Dormant')}>
                Dormant
              </button>

              {/* Archived */}
              <button className={currentFilter === 'Archived' ? 'Filter-Primary' : 'Filter-Secondary'} onClick={() => setCurrentFilter('Archived')}>
                Archived
              </button>

              {/* Decommissioned */}
              <button className={currentFilter === 'Decommissioned' ? 'Filter-Primary' : 'Filter-Secondary'} onClick={() => setCurrentFilter('Decommissioned')}>
                Decommissioned
              </button>

              {/* Draft */}
              <button className={currentFilter === 'Draft' ? 'Filter-Primary' : 'Filter-Secondary'} onClick={() => setCurrentFilter('Draft')}>
                Draft
              </button>

              {/* All */}
              <button className={currentFilter === 'All' ? 'Filter-Primary' : 'Filter-Secondary'} onClick={() => setCurrentFilter('All')}>
                All
              </button>

            </div>

            <div className='flex flex-row items-center gap-2'>

              {/* Search Bar */}
              <div className='grid grid-cols-[30px_1fr] items-center gap-[15px] bg-white px-[5px] rounded-[5px] border-1 border-solid border-[#D8D8D8] max-w-[300px]'>
                <img className='ml-[10px]' src={IconSearch} alt='searchInput-icon'></img>
                <label htmlFor='searchInput'>
                  <input
                    id='searchInput'
                    className='border-none h-[40px] p-0 m-0 outline-none'
                    type='text'
                    placeholder='Search'
                    onChange={(e) => handleSearch(e.target.value)}
                    value={searchInput}
                  ></input>
                </label>
              </div>

              {/* Add Application */}
              {
                // Role check to create new applications
                (roleStatus === 'create' || roleStatus === 'write' || roleStatus === 'delete' || roleStatus === 'admin') ?
                  <AddApplication></AddApplication>
                  :
                  <button disabled className='Primary-Button whitespace-nowrap flex flex-row gap-2'>
                    <img src={IconAdd} alt='Add'></img> Add Application
                  </button>
              }

              {/* Download IT Report */}
              <ExportToCSV
                filename={`Applications-${Date.now()}`}
                data={getDataObject(applicationsFiltered)}
                disabled={!(roleStatus === 'download' || roleStatus === 'create' || roleStatus === 'write' || roleStatus === 'delete' || roleStatus === 'admin')}
              ></ExportToCSV>

              {/* Download All Fields */}
              <ExportToCSV
                filename={`Applications-Bulk-Export-${Date.now()}`}
                data={getBulkExportData(applicationsFiltered)}
                title={'Bulk Export'}
                disabled={!(roleStatus === 'download' || roleStatus === 'create' || roleStatus === 'write' || roleStatus === 'delete' || roleStatus === 'admin')}
              ></ExportToCSV>

            </div>
          </div>

          {/* ----------------------------------- */}
          {/*  Table Container                    */}
          {/* ----------------------------------- */}

          <FilterTags
            tableArray={applications}
            setFilteredTableArray={setApplicationsFiltered}
          ></FilterTags>

          <div className='Table-Container flex flex-col w-full h-full overflow-hidden'>

            {/* ======== Table Filters ========== */}
            <div className='flex flex-row justify-between py-[15px] px-[20px] rounded-tl-[10px] rounded-tr-[10px] border-b-[#D8D8D8] border-solid border-b bg-[#F5F5F5]'>

              <label className='font-semibold self-center'>
                Total: {applicationsFiltered.length} of {applications.length}
              </label>

            </div>

            {/* Makes the table scrollable */}
            <div className='w-full overflow-auto'>

              <FilterByPane
                tableArray={applications}
              ></FilterByPane>

              <table className='w-full min-h-[350px]'>
                <thead>
                  <tr className='sticky top-0 bg-white border-b-[#D8D8D8] border-solid border-b z-10 box-border overflow-visible'>

                    {/* Application Name (Sort/Filter) */}
                    <TableHeader
                      column='Application Name'
                      styleInput={{ minWidth: '120px' }}
                      objectKey='applicationname'
                      sticky={true}
                      setSearchInput={setSearchInput}
                    ></TableHeader>

                    {/* Description */}
                    <th className='py-[15px] px-[10px] font-medium whitespace-wrap'>Description</th>

                    {/* Primary Beneficiary */}
                    <TableHeader
                      column='Primary Beneficiary'
                      objectKey='primarybeneficiary'
                      setSearchInput={setSearchInput}
                    ></TableHeader>

                    {/* Vendor (Sort/Filter) */}
                    <TableHeader
                      column='Vendor'
                      objectKey='vendor.vendorname'
                      setSearchInput={setSearchInput}
                    ></TableHeader>

                    {/* Business Owner */}
                    <th className='py-[15px] px-[10px] font-medium whitespace-nowrap'>Business Owner</th>

                    {/* IT Owner */}
                    <th className='py-[15px] px-[10px] font-medium whitespace-nowrap'>IT Owner</th>

                    {/* Supported By */}
                    <TableHeader
                      column='Supported By'
                      objectKey='supportedby'
                      setSearchInput={setSearchInput}
                    ></TableHeader>

                    {/* Status */}
                    <th className='py-[15px] px-[10px] font-medium whitespace-nowrap'>Status</th>

                  </tr>
                </thead>
                <tbody>
                  {

                    // No applications found
                    applicationsFiltered.length === 0 ?
                      (
                        <tr>
                          <td colSpan={14}>
                            <div className='text-center p-[2%]'>
                              No applications found.
                            </div>
                          </td>
                        </tr>
                      )
                      :
                      (
                        // Map in each commercial application
                        applicationsFiltered.map((application, index) => (

                          <tr key={index} className={`border-b-[#C8C8C9] border-solid border-b last:border-0 hover:bg-[#F0F7F7] ${applicationsFiltered.length === 1 && 'h-[250px]'}`}>

                            {/* Application Name */}
                            <td className='align-top py-[15px] px-[10px] left-0 drop-shadow-md bg-white sticky font-medium cursor-pointer hover:underline max-w-[30ch] whitespace-nowrap overflow-hidden text-ellipsis' onClick={() => navigate(`/inventory/application/${application.applicationid}?view=overview`)}>
                              {application?.applicationname}
                            </td>

                            {/* Description */}
                            <td className='align-top py-[15px] px-[10px] border-solid border-r text-nowrap max-w-[60ch] overflow-hidden whitespace-wrap text-ellipsis'>
                              {application?.applicationdescription}
                            </td>

                            {/* Primary Beneficiary */}
                            <td className='align-top py-[15px] px-[10px] border-solid border-r text-nowrap max-w-[40ch] overflow-hidden whitespace-wrap text-ellipsis'>
                              {application?.primarybeneficiary}
                            </td>

                            {/* Vendor Name */}
                            <td className='align-top py-[15px] px-[10px] border-solid border-r  text-nowrap max-w-[40ch] overflow-hidden whitespace-wrap text-ellipsis'>
                              {application.vendor.vendorname}
                            </td>

                            {/* Business Owner (Editable) */}
                            <td className='align-top py-[15px] px-[10px] border-solid border-r'>
                              <LabelList
                                objectList={application?.businessowner}
                                object={application}
                                index={`${index}-0`}
                                setSidePane={setBusinessOwnerPane}
                              ></LabelList>
                            </td>

                            {/* IT Owner (Editable) */}
                            <td className='align-top py-[15px] px-[10px] border-solid border-r'>
                              <LabelList
                                objectList={application?.itowner}
                                object={application}
                                index={`${index}-1`}
                                setSidePane={setITOwnerPane}
                              ></LabelList>
                            </td>

                            {/* Supported By */}
                            <td className='align-top py-[15px] px-[10px] border-solid border-r text-nowrap max-w-[40ch]'>
                              {application?.supportedby}
                            </td>

                            {/* Status */}
                            <td className='align-top py-[15px] px-[10px] border-solid border-r'>
                              <StatusLabel status={application?.applicationstatus}></StatusLabel>
                            </td>

                          </tr>

                        ))
                      )
                  }
                </tbody>
              </table>

            </div>

          </div>

          {/* ----------------------------------- */}
          {/*  Side Panes                         */}
          {/* ----------------------------------- */}

          <UserPane
            title={subscriberPane?.applicationname}
            vendorname={subscriberPane?.vendor?.vendorname}
            collectionid={'applications'}
            documentid={subscriberPane?.applicationid}
            fieldPath={'subscribed'}
            defaultUsers={subscriberPane?.subscribed}
            paneOpen={subscriberPane}
            setPaneOpen={setSubscriberPane}
          ></UserPane>

          <UserPane
            title={businessOwnerPane?.applicationname}
            vendorname={businessOwnerPane?.vendor?.vendorname}
            collectionid={'applications'}
            documentid={businessOwnerPane?.applicationid}
            fieldPath={'businessowner'}
            defaultUsers={businessOwnerPane?.businessowner}
            paneOpen={businessOwnerPane}
            setPaneOpen={setBusinessOwnerPane}
          ></UserPane>

          <UserPane
            title={ITOwnerPane?.applicationname}
            vendorname={ITOwnerPane?.vendor?.vendorname}
            collectionid={'applications'}
            documentid={ITOwnerPane?.applicationid}
            fieldPath={'itowner'}
            defaultUsers={ITOwnerPane?.itowner}
            paneOpen={ITOwnerPane}
            setPaneOpen={setITOwnerPane}
          ></UserPane>

        </div>

      }

    ></PageComponent>
  )
}
